import { useEffect, useState } from 'react';
import {
  ROLES_EQUIPO,
  type RolEquipo,
  type UsuarioOperador,
  type AuditEvent,
} from '@sumarios/shared';
import * as api from './api.js';
import { mensajeError } from './App.js';

const etiquetas: Record<RolEquipo, string> = {
  OPERADOR_FINANZAS: 'Finanzas — contratos, facturas y pagos',
  OPERADOR_SOPORTE: 'Soporte — tickets y seguimiento',
  OPERADOR_LECTURA: 'Lectura — consulta y auditoría, sin cambios',
};
export function EquipoOperador({ token }: { token: string }) {
  const [usuarios, setUsuarios] = useState<UsuarioOperador[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [eventos, setEventos] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string>();
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [alta, setAlta] = useState(false);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<RolEquipo>('OPERADOR_LECTURA');
  const [motivo, setMotivo] = useState('');
  const [edicion, setEdicion] = useState<{
    usuario: UsuarioOperador;
    accion: 'roles' | 'estado' | 'invitacion';
  }>();
  async function cargar(mas = false) {
    const [lista, audit] = await Promise.all([
      api.listarEquipo(token, mas ? cursor : undefined),
      api.auditEquipo(token),
    ]);
    setUsuarios((prev) => (mas ? [...prev, ...lista.usuarios] : lista.usuarios));
    setCursor(lista.cursor);
    setEventos(audit.eventos);
  }
  useEffect(() => {
    void cargar().catch((e) => setError(mensajeError(e)));
  }, [token]);
  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setOcupado(true);
    setError(undefined);
    setAviso('');
    try {
      if (edicion)
        await api.cambiarOperador(token, edicion.usuario.userId, edicion.accion, {
          motivo,
          ...(edicion.accion === 'roles' ? { rol } : {}),
          ...(edicion.accion === 'estado' ? { habilitado: !edicion.usuario.habilitado } : {}),
        });
      else await api.crearOperador(token, { email, nombre, rol, motivo });
      setAviso(
        alta || edicion?.accion === 'invitacion'
          ? 'Invitación enviada por correo. El usuario deberá cambiar su clave y configurar MFA.'
          : 'Acceso actualizado. Los permisos cambian en la próxima operación del usuario.',
      );
      setAlta(false);
      setEdicion(undefined);
      setMotivo('');
      setEmail('');
      setNombre('');
      await cargar();
    } catch (e) {
      setError(mensajeError(e));
      // Una invitación puede fallar después del alta: mostrar la cuenta permite recuperarla.
      await cargar().catch(() => undefined);
    } finally {
      setOcupado(false);
    }
  }
  function editar(usuario: UsuarioOperador, accion: 'roles' | 'estado' | 'invitacion') {
    setAlta(false);
    setEdicion({ usuario, accion });
    setRol(ROLES_EQUIPO.find((r) => usuario.roles.includes(r)) ?? 'OPERADOR_LECTURA');
    setMotivo('');
    setError(undefined);
    setAviso('');
  }
  return (
    <section className="tarjeta col">
      <div className="fila-entre">
        <h2>Equipo IntiKipu</h2>
        <button
          disabled={ocupado}
          onClick={() => {
            setAlta(true);
            setEdicion(undefined);
            setMotivo('');
            setRol('OPERADOR_LECTURA');
          }}
        >
          + Nuevo usuario interno
        </button>
      </div>
      <p className="ayuda">
        Accesos internos al panel de Instructor. Finanzas y soporte pueden modificar su área;
        lectura solo permite consultar. Estos usuarios no acceden a expedientes de las
        instituciones.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {aviso && <p role="status">{aviso}</p>}
      {(alta || edicion) && (
        <form className="subtarjeta col" onSubmit={guardar}>
          <h3>
            {alta
              ? 'Nuevo usuario interno'
              : `${edicion?.accion === 'roles' ? 'Cambiar rol' : edicion?.accion === 'invitacion' ? 'Reenviar invitación' : edicion?.usuario.habilitado ? 'Suspender acceso' : 'Reactivar acceso'}: ${edicion?.usuario.email}`}
          </h3>
          {alta && (
            <>
              <label>
                Nombre completo
                <input
                  required
                  maxLength={100}
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </label>
              <label>
                Correo del usuario
                <input
                  type="email"
                  required
                  maxLength={254}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            </>
          )}
          {(alta || edicion?.accion === 'roles') && (
            <label>
              Rol interno
              <select value={rol} onChange={(e) => setRol(e.target.value as RolEquipo)}>
                {ROLES_EQUIPO.map((r) => (
                  <option key={r} value={r}>
                    {etiquetas[r]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Motivo del cambio
            <input
              required
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="fila">
            <button disabled={ocupado || !motivo.trim()} type="submit">
              {ocupado ? 'Guardando…' : alta ? 'Crear y enviar invitación' : 'Confirmar cambio'}
            </button>
            <button
              type="button"
              className="secundario"
              disabled={ocupado}
              onClick={() => {
                setAlta(false);
                setEdicion(undefined);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
      <div className="tabla-scroll" role="region" aria-label="Usuarios internos" tabIndex={0}>
        <table className="tabla">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              <th>Acceso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.userId}>
                <td>
                  {u.nombre || u.email}
                  <br />
                  {u.nombre && u.email}
                </td>
                <td>
                  {u.roles
                    .map((r) => (r === 'OPERADOR_ADMIN' ? 'Administrador' : etiquetas[r]))
                    .join(', ') || 'Sin rol asignado'}
                </td>
                <td>
                  {!u.habilitado
                    ? 'Suspendido'
                    : u.estado === 'FORCE_CHANGE_PASSWORD'
                      ? 'Invitación pendiente'
                      : 'Activo'}
                </td>
                <td>
                  {u.roles.includes('OPERADOR_ADMIN') ? (
                    <span className="ayuda">Administrador protegido</span>
                  ) : (
                    <div className="fila">
                      <button
                        disabled={ocupado}
                        className="secundario"
                        onClick={() => editar(u, 'roles')}
                      >
                        Cambiar rol
                      </button>
                      <button
                        disabled={ocupado}
                        className="secundario"
                        onClick={() => editar(u, 'estado')}
                      >
                        {u.habilitado ? 'Suspender' : 'Reactivar'}
                      </button>
                      {u.estado === 'FORCE_CHANGE_PASSWORD' && (
                        <button
                          disabled={ocupado}
                          className="secundario"
                          onClick={() => editar(u, 'invitacion')}
                        >
                          Reenviar invitación
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor && (
        <button
          className="secundario"
          disabled={ocupado}
          onClick={() => {
            void cargar(true).catch((e) => setError(mensajeError(e)));
          }}
        >
          Cargar más usuarios
        </button>
      )}
      <h3>Historial de accesos internos</h3>
      {eventos.length === 0 ? (
        <p className="ayuda">Sin cambios registrados.</p>
      ) : (
        <ul>
          {eventos
            .slice(-30)
            .reverse()
            .map((e) => (
              <li key={e.logId}>
                <strong>
                  {e.accion === 'OPERADOR_CAMBIO_SOLICITADO'
                    ? 'Cambio solicitado'
                    : e.accion === 'OPERADOR_CAMBIO_COMPLETADO'
                      ? 'Cambio completado'
                      : 'Cambio fallido'}
                </strong>{' '}
                · {e.timestamp} · {e.actor.nombre}
                <p className="ayuda">{descripcionEvento(e, usuarios)}</p>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

function descripcionEvento(evento: AuditEvent, usuarios: UsuarioOperador[]): string {
  try {
    const d = JSON.parse(evento.detalle) as {
      usuario: string;
      motivo: string;
      rol?: RolEquipo;
      habilitado?: boolean;
      ruta: string;
    };
    const usuario = usuarios.find((u) => u.userId === d.usuario)?.email ?? d.usuario;
    const accion =
      d.ruta === 'POST /operadores'
        ? 'Alta'
        : d.ruta.endsWith('/roles')
          ? 'Cambio de rol'
          : d.ruta.endsWith('/invitacion')
            ? 'Invitación'
            : d.habilitado
              ? 'Reactivación'
              : 'Suspensión';
    return `${accion} de ${usuario}${d.rol ? ' · ' + etiquetas[d.rol] : ''} · Motivo: ${d.motivo}`;
  } catch {
    return evento.detalle;
  }
}
